import { immerable } from "immer"
import { useState } from 'react'
import api from '@/services/api/serviceProviderRelation.js';
import useComputed from "../use-computed/index";
import rem from "@/utils/rem";
import { useSelector } from 'react-redux';
import MultiLevelDropDown from "@/pages/onboarding/components/MultiLevelMenuDropDown/index";
import { renderStatus } from "@/utils/onboarding-status-handler";
import titleCase from "@/utils/title-case";
import { statusIdMapper } from "@/pages/onboarding/helpers/index";

class ServiceProvider {
	[immerable] = true
	constructor(params) {
		this.serviceProviderRelationshipId = params.serviceProviderRelationshipId;
		this.dataOwnerOrgId = params.dataOwnerOrgId;
		this.dataElementId = params.dataElementId;
		this.dataElementName = params.dataElementName;
		this.serviceProviderRelationshipStatus = titleCase(params.serviceProviderRelationshipStatus);
		this.serviceProviderRelationshipInvitesId = params.serviceProviderRelationshipInvitesId;
		this.initiater = params.initiater;
		this.created = params.created;
		this.modified = params.modified;
		this.flow = params.flow;
		this.serviceProviderEmail = params.serviceProviderEmail;
		this.serviceProviderOrgId = params.serviceProviderOrgId;
		this.serviceProviderOrgName = params.serviceProviderOrgName;
		this.dataOwnerOrgName = params.dataOwnerOrgName;
		this.ownSystems = params.ownSystems;
		this.status = params.initiater ==='SERVICE_PROVIDER' && params.dataOwnerOrgId !==params.orgId && params.serviceProviderRelationshipStatus?.toUpperCase() ==='PENDING' ? 'PENDING YOUR APPROVAL': params.serviceProviderRelationshipStatus?.toUpperCase();
		this.statusCode = statusIdMapper[this.status?.toUpperCase()];
	}
}

const useServiceProviderRelationship = (handleAction) => {

	//  CONSTANTS
	const statusEnum = {
		APPROVED: "APPROVED",
		CANCELLED: "CANCELLED",
		PENDING: "PENDING",
		REVOKED: "REVOKED",
		REJECTED: "REJECTED",
	}

	//  STATES
	const [serviceProviderData, setServiceProviderData] = useComputed([])
	const [serviceProviderError, setServiceProviderError] = useState();
	const [loading, setLoading] = useState(false);

	const profile = useSelector(state => state.auth.profile)

	function serialize(params) {
		return {
			serviceProviderRelationshipId: params.serviceProviderRelationshipId,
			dataOwnerOrgId: params.dataOwnerOrgId,
			dataElementId: params.dataElementId,
			dataElementName: params.dataElementName,
			serviceProviderRelationshipStatus: titleCase(params.serviceProviderRelationshipStatus),
			serviceProviderRelationshipInvitesId: params.serviceProviderRelationshipInvitesId,
			initiater: params.initiater,
			created: params.created,
			modified: params.modified,
			flow: params.flow,
			serviceProviderEmail: params.serviceProviderEmail,
			serviceProviderOrgId: params.serviceProviderOrgId,
			serviceProviderOrgName: params.serviceProviderOrgName,
			dataOwnerOrgName: params.dataOwnerOrgName,
			ownSystems: params.ownSystems,
			orgId: params.orgId
		}
	}

	// Fetch Service Provider data
	const fetchServiceProviderData = async (params) => {
		setLoading(true);
		const [error, response] = await api.index(params);
		setLoading(false);
		if (error) {
			return setServiceProviderError('Solution Provider API has error')
		}
		const serializedData = response.map(entry => serialize(entry))
		const newItems = serializedData.map(params => new ServiceProvider(params))
		setServiceProviderData(newItems);
		return newItems;
	}

	const renderSPOrgName = (_orgName, rowData) => {
		const { serviceProviderOrgName, serviceProviderOrgId, serviceProviderEmail } = rowData;
		if (serviceProviderOrgId === profile?.organizationId) {
			return 'Myself';
		}

		return (
			serviceProviderOrgName ?? serviceProviderEmail
		);
	}

	const renderSPAction = (_action, rowData) => {
		const menuList = [];
		let hasNotifyDot = false;
		// ACTION based on statusEnum
		let { serviceProviderRelationshipStatus, dataOwnerOrgId, serviceProviderOrgId, initiater } = rowData
		serviceProviderRelationshipStatus = serviceProviderRelationshipStatus?.toUpperCase();
		if (serviceProviderRelationshipStatus === "APPROVED") {
			menuList.push({
				label: "Revoke",
				onClick: (e) => {
					e.stopPropagation();
					handleAction(rowData, "REVOKED");
				}
			})
		}
		else if (serviceProviderRelationshipStatus === "PENDING") {
			if ((initiater === "DATA_OWNER" && profile?.organizationId === dataOwnerOrgId) || (initiater === "SERVICE_PROVIDER" && profile?.organizationId === serviceProviderOrgId)) {
				menuList.push({
					label: "Cancel Request",
					onClick: (e) => {
						e.stopPropagation();
						handleAction(rowData, "CANCELLED");
					}
				})
			}
			else {
				menuList.push({
					label: "Approve",
					onClick: (e) => {
						e.stopPropagation();
						handleAction(rowData, "APPROVED");
					}
				},
					{
						label: "Reject",
						onClick: (e) => {
							e.stopPropagation();
							handleAction(rowData, "REJECTED");
						}
					}
				)
				hasNotifyDot = true;
			}
		}

		return (
			menuList?.length > 0 && <div className='on-boarding__action-items'>
				<MultiLevelDropDown
					menuList={menuList}
					hasNotifyDot={hasNotifyDot}
					id="my-service-provider-action-menu"
				/>
			</div>
		);
	}

	const renderFlowStatus = (flow) => {
		switch (flow) {
			case "SEND":
				return "Provide on your behalf";
			case "RECEIVE":
				return "Consume on your behalf";
			default:
				return "Provide on your behalf";
		}
	}

	const serviceProviderHeader = [
		{
			key: "serviceProviderOrgName",
			label: "Solution Provider",
			width: rem(180),
			render: (orgName, rowData) => renderSPOrgName(orgName, rowData),
		},
		{
			key: 'dataElementName',
			label: 'Data Element',
			width: rem(180)
		},
		{
			key: 'flow',
			label: 'Provide/Consume',
			width: rem(180),
			render: (flow) => renderFlowStatus(flow)
		},
		{
			key: 'modified',
			label: 'Modified',
			width: rem(180),
			render: (modified) => new Date(modified).toLocaleDateString(),
		},
		{
			key: "serviceProviderRelationshipStatus",
			label: "Status",
			width: rem(180),
			render: (status, rowData) => renderStatus(status?.toUpperCase(), rowData, profile),
		},
		{
			key: "action",
			width: rem(96),
			sortable: false,
			render: (action, rowData) => renderSPAction(action, rowData)
		},
	];

	return serviceProviderData ? {
		fetchServiceProviderData,
		statusEnum,
		renderSPAction,
		renderSPOrgName,
		renderFlowStatus,
		serviceProviderData,
		serviceProviderHeader,
		serviceProviderError,
		loading
	} : {}
}

export default useServiceProviderRelationship;

